import { useEffect, useState, useRef } from "react";
import "./MobileSolutions.css";
import ContactForm from "../../components/ContactUs/ContactUs";
import {
  Smartphone,
  Tablet,
  ShoppingCart,
  Package,
  Route,
  BarChart3,
  CheckCircle,
} from "lucide-react";

const MobileSolutions = () => {
  const [showContact, setShowContact] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());

  const sectionRefs = {
    hero: useRef(null),
    nav: useRef(null),
    modus: useRef(null),
    planning: useRef(null),
    audit: useRef(null),
    features: useRef(null),
    cta: useRef(null),
  };

  // Scroll to top and trigger animations
  useEffect(() => {
    window.scrollTo(0, 0);

    // Trigger hero animations
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionName = entry.target.getAttribute("data-section");
          if (sectionName) {
            setVisibleSections((prev) => new Set([...prev, sectionName]));
          }
        }
      });
    }, observerOptions);

    // Observe all sections
    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => observer.disconnect();
  }, []);

  const modusFunctionalities = [
    {
      icon: ShoppingCart,
      title: "Selling",
      description:
        "We can also sell via the mobile app, where you can manage sales processes, create quotes, and process orders directly from your mobile device.",
    },
    {
      icon: Route,
      title: "Routing Plans",
      description:
        "Routes are set up by enterprise managers to optimize delivery schedules and ensure efficient logistics management.",
    },
    {
      icon: Package,
      title: "Registration of Goods",
      description:
        "Registering one of the modules that most efficiently handles inventory tracking and goods management processes.",
    },
  ];

  const coreModules = [
    {
      title: "Planning and Open-to-Buy Budget",
      description:
        "Strategic budget planning and allocation for optimal inventory investment.",
    },
    {
      title: "Purchase Planning and Allocation",
      description:
        "Efficient purchase planning with smart allocation across locations.",
    },
    {
      title: "Product Forecasting and Replenishment",
      description:
        "Accurate demand forecasting and automated replenishment processes.",
    },
  ];

  const keyFeatures = [
    "Flexible setup and functionality based on the company's needs",
    "Based on the NAV business logic",
    "On-line and off-line access",
    "Possibility to filter logged sales employees",
    "Eliminating task redundancies and paper work",
    "Integrated phonebook - importing and exporting data",
    "Using built-in device capabilities (phone, e-mail, browser, map)",
    "Direct entry of quotation and order data",
    "Barcode scanning",
    "Direct printing",
    "Reporting capabilities",
    "Multiple languages",
  ];

  const auditTopics = [
    "Control measures for the financial and reporting, purchase, sales, inventory and project administration processes",
    "The relevance and examples of IT general controls like security, IT continuity, and change management",
    "IT general controls concerning Dynamics NAV",
    "Segregation of duties setup and user rights management",
  ];

  return (
    <div className="mobile-solutions-main-container">
      {/* Hero Section */}
      <section
        className="mobile-solutions-hero-wrapper"
        ref={sectionRefs.hero}
        data-section="hero"
      >
        <div className="mobile-solutions-hero-background">
          <div className="mobile-solutions-floating-elements">
            <div className="mobile-solutions-floating-element mobile-solutions-element-1"></div>
            <div className="mobile-solutions-floating-element mobile-solutions-element-2"></div>
            <div className="mobile-solutions-floating-element mobile-solutions-element-3"></div>
          </div>
        </div>

        <div className="mobile-solutions-content-width">
          <div className="mobile-solutions-hero-layout">
            <div className="mobile-solutions-hero-content">
              <div
                className={`mobile-solutions-tagline ${
                  isVisible ? "mobile-solutions-slide-down" : ""
                }`}
              >
                <span className="mobile-solutions-tagline-text">
                  Smarter workflows,
                </span>
                <span className="mobile-solutions-tagline-highlight">
                  wherever you go.
                </span>
              </div>
            </div>
            <div className="mobile-solutions-hero-main">
              <div
                className={`mobile-solutions-hero-icon ${
                  isVisible ? "mobile-solutions-scale-in" : ""
                }`}
              >
                <Tablet className="mobile-solutions-hero-tablet-icon" />
                <div className="mobile-solutions-pulse-ring"></div>
              </div>
              <div className="mobile-solutions-hero-text">
                <h1
                  className={`mobile-solutions-hero-title ${
                    isVisible ? "mobile-solutions-slide-up" : ""
                  }`}
                >
                  MOBILE SOLUTIONS
                </h1>
                <p
                  className={`mobile-solutions-hero-description ${
                    isVisible ? "mobile-solutions-fade-in" : ""
                  }`}
                >
                  Making the mobile access to an ERP system possible means a
                  breakthrough for its users. Mobile-first and cloud-first
                  solution are the key concepts for Microsoft Dynamics NAV. With
                  all data on hand and easy connection to databases, Dynamics
                  users can get insight into reports, decisions and documents
                  they need while working outside the company. And vice versa,
                  mobile employees can instantly upload data their colleagues
                  working at the office need. Native integration with Office 365
                  ensures even more benefits.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modus Functionalities Section */}
      <section
        className="mobile-solutions-modus-section"
        ref={sectionRefs.modus}
        data-section="modus"
      >
        <div className="mobile-solutions-content-width">
          <div className="mobile-solutions-center-align">
            <h2
              className={`mobile-solutions-modus-title ${
                visibleSections.has("modus") ? "mobile-solutions-slide-up" : ""
              }`}
            >
              Modul Functionalities
            </h2>
          </div>

          <div className="mobile-solutions-modus-grid">
            {modusFunctionalities.map((functionality, index) => {
              const Icon = functionality.icon;
              return (
                <div
                  key={index}
                  className={`mobile-solutions-modus-card ${
                    visibleSections.has("modus")
                      ? "mobile-solutions-card-slide-up"
                      : ""
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="mobile-solutions-modus-card-header">
                    <div className="mobile-solutions-modus-card-icon">
                      <Icon className="mobile-solutions-medium-icon" />
                    </div>
                    <h3 className="mobile-solutions-modus-card-title">
                      {functionality.title}
                    </h3>
                  </div>
                  <div className="mobile-solutions-modus-card-body">
                    <p className="mobile-solutions-modus-card-description">
                      {functionality.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Merchandise Planning Section */}
      <section
        className="mobile-solutions-planning-section"
        ref={sectionRefs.planning}
        data-section="planning"
      >
        <div className="mobile-solutions-content-width">
          <div className="mobile-solutions-planning-content">
            <h2
              className={`mobile-solutions-planning-title ${
                visibleSections.has("planning")
                  ? "mobile-solutions-slide-up"
                  : ""
              }`}
            >
              Integrated Merchandise Planning, Retail Forecasting, Store
              Allocation and Replenishment
            </h2>
            <p
              className={`mobile-solutions-planning-description ${
                visibleSections.has("planning")
                  ? "mobile-solutions-fade-in"
                  : ""
              }`}
              style={{ animationDelay: "0.1s" }}
            >
              The success of a retail business hinges on accurate forecasting
              and inventory planning that meets customer demands at the right
              time and place. Merchandise Planning and Execution provides
              functionality for all stages of merchandising — from budget
              planning and demand forecasting to purchase planning, allocation
              and replenishment.
            </p>
            <p
              className={`mobile-solutions-planning-description ${
                visibleSections.has("planning")
                  ? "mobile-solutions-fade-in"
                  : ""
              }`}
              style={{ animationDelay: "0.2s" }}
            >
              The demand-driven solution is designed to let you plan
              assortments, optimize product allocation, develop effective
              pricing and mark-down strategies for faster sell-through and
              accurately replenish inventory — all to reduce operating costs and
              increase sales.
            </p>

            <h3
              className={`mobile-solutions-modules-subtitle ${
                visibleSections.has("planning")
                  ? "mobile-solutions-slide-up"
                  : ""
              }`}
              style={{ animationDelay: "0.3s" }}
            >
              The solution combines three core modules including:
            </h3>
            <div className="mobile-solutions-modules-grid">
              {coreModules.map((module, index) => (
                <div
                  key={index}
                  className={`mobile-solutions-module-item ${
                    visibleSections.has("planning")
                      ? "mobile-solutions-slide-right"
                      : ""
                  }`}
                  style={{ animationDelay: `${0.4 + index * 0.1}s` }}
                >
                  <div className="mobile-solutions-module-icon">
                    <BarChart3 className="mobile-solutions-small-icon" />
                  </div>
                  <div className="mobile-solutions-module-content">
                    <h4 className="mobile-solutions-module-title">
                      {module.title}
                    </h4>
                    <p className="mobile-solutions-module-description">
                      {module.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* IT Audit Section */}
      <section
        className="mobile-solutions-audit-section"
        ref={sectionRefs.audit}
        data-section="audit"
      >
        <div className="mobile-solutions-content-width">
          <div className="mobile-solutions-audit-content">
            <h2
              className={`mobile-solutions-audit-title ${
                visibleSections.has("audit") ? "mobile-solutions-slide-up" : ""
              }`}
            >
              IT Audit and Control Framework
            </h2>
            <p
              className={`mobile-solutions-audit-description ${
                visibleSections.has("audit") ? "mobile-solutions-fade-in" : ""
              }`}
              style={{ animationDelay: "0.1s" }}
            >
              Information Technologies (IT) platforms and systems with the
              corresponding processes and procedures are part of the research
              scope of our IT audit. This scope allows us to provide you with
              insight in the optimisation possibilities concerning the use of
              Navision ERP and the reliability of the (financial) data
              processing. For this purpose we use a control framework and audit
              tools developed especially for Navision ERP.
            </p>

            <h3
              className={`mobile-solutions-audit-subtitle ${
                visibleSections.has("audit") ? "mobile-solutions-slide-up" : ""
              }`}
              style={{ animationDelay: "0.2s" }}
            >
              The following topics will be covered:
            </h3>
            <div className="mobile-solutions-audit-list">
              {auditTopics.map((topic, index) => (
                <div
                  key={index}
                  className={`mobile-solutions-audit-item ${
                    visibleSections.has("audit")
                      ? "mobile-solutions-slide-right"
                      : ""
                  }`}
                  style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                >
                  <CheckCircle className="mobile-solutions-check-icon" />
                  <span className="mobile-solutions-audit-text">{topic}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section
        className="mobile-solutions-features-section"
        ref={sectionRefs.features}
        data-section="features"
      >
        <div className="mobile-solutions-content-width">
          <div className="mobile-solutions-center-align">
            <h2
              className={`mobile-solutions-features-title ${
                visibleSections.has("features")
                  ? "mobile-solutions-zoom-in"
                  : ""
              }`}
            >
              Key Features & Capabilities
            </h2>
            <p
              className={`mobile-solutions-features-description ${
                visibleSections.has("features")
                  ? "mobile-solutions-fade-in"
                  : ""
              }`}
              style={{ animationDelay: "0.1s" }}
            >
              Comprehensive mobile functionality designed to enhance
              productivity and streamline business processes.
            </p>
          </div>

          <div className="mobile-solutions-features-grid">
            {keyFeatures.map((feature, index) => (
              <div
                key={index}
                className={`mobile-solutions-feature-item ${
                  visibleSections.has("features")
                    ? "mobile-solutions-feature-slide-up"
                    : ""
                }`}
                style={{ animationDelay: `${0.2 + index * 0.05}s` }}
              >
                <CheckCircle className="mobile-solutions-feature-check-icon" />
                <span className="mobile-solutions-feature-text">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="mobile-solutions-cta-section"
        ref={sectionRefs.cta}
        data-section="cta"
      >
        <div className="mobile-solutions-content-width mobile-solutions-center-align">
          <h2
            className={`mobile-solutions-cta-title ${
              visibleSections.has("cta") ? "mobile-solutions-zoom-in" : ""
            }`}
          >
            Ready to Mobilize Your Business?
          </h2>
          <p
            className={`mobile-solutions-cta-description ${
              visibleSections.has("cta") ? "mobile-solutions-fade-in" : ""
            }`}
            style={{ animationDelay: "0.1s" }}
          >
            Transform your business operations with our comprehensive mobile
            solutions for Microsoft Dynamics NAV. Get started today and
            experience the power of mobile-first ERP.
          </p>
          <div
            className={`mobile-solutions-cta-buttons ${
              visibleSections.has("cta") ? "mobile-solutions-bounce-in" : ""
            }`}
            style={{ animationDelay: "0.2s" }}
          >
            <button
              className="mobile-solutions-primary-button mobile-solutions-pulse-button"
              onClick={() => setShowContact(true)}
            >
              <Smartphone className="mobile-solutions-small-icon" />
              Get Mobile Solutions
            </button>
          </div>
        </div>
      </section>

      {/* Contact Form Modal */}
      {showContact && (
        <div
          className="modal-overlay mobile-solutions-modal-fade-in"
          onClick={() => setShowContact(false)}
        >
          <div
            className="modal-content mobile-solutions-modal-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={() => setShowContact(false)}
            >
              ✕
            </button>
            <ContactForm />
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileSolutions;
